import NumberFlow from "@number-flow/react"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { cn } from "design-system/utils"
import { Check } from "lucide-react"
import { useContactMutation } from "modules/subscription/hooks/useContactMutation"
import { useCreateCheckoutSessionMutation } from "modules/subscription/hooks/useCreateCheckoutSessionMutation"
import { useTranslation } from "react-i18next"
import { CheckoutSessionModeEnum, ListProductsResponseDataSchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"

type Props = {
  frequency: "month" | "year"
  name: string
  description: string
  features: string[] | Readonly<string[]>
  prices: z.infer<typeof ListProductsResponseDataSchema>[number]["prices"]
  isPopular: boolean
  isActive: boolean
  canUpgrade: boolean
  isContactUs: boolean
}

export const PricingCard = (props: Props) => {
  const {
    name,
    description,
    features,
    prices,
    frequency,
    isPopular,
    isActive,
    isContactUs,
    canUpgrade,
  } = props
  const { t, i18n } = useTranslation()

  const price = prices.find((p) => p.amount === 0 || p.interval === frequency)

  return (
    <div
      className={cn(
        "relative flex flex-col gap-6 overflow-visible rounded-2xl border p-6 mb-6 bg-background text-foreground shadow w-full max-w-[250px] min-h-[360px]",
        isPopular && "border-foreground border-2",
      )}
    >
      {isPopular && (
        <Badge
          variant="default"
          className="absolute h-6 -top-6 left-1/2 transform -translate-x-1/2 rounded-b-none w-fit z-10"
        >
          {t("subscription.plans.popular")}
        </Badge>
      )}
      <h2 className="flex items-center justify-between gap-3 text-xl font-medium capitalize">
        <span>{name}</span>
        {isActive && <Badge variant="secondary">{t("subscription.plans.current")}</Badge>}
      </h2>
      <div className="flex flex-col gap-0 items-start justify-center min-h-18">
        {!isContactUs && price && price.amount && price.amount > 0 ? (
          <>
            <NumberFlow
              format={{
                style: "currency",
                currency: price.currency,
                trailingZeroDisplay: "stripIfInteger",
                localeMatcher: "lookup",
              }}
              locales={i18n.language}
              value={Math.round(price.amount / (frequency === "month" ? 1 : 12))}
              className="text-4xl font-medium"
            />
            <p className="text-xs -mt-1 font-medium">{t("subscription.plans.perMonth")}</p>
          </>
        ) : (
          <h1 className="text-4xl font-medium">
            {isContactUs ? t("subscription.plans.contactUs") : t("subscription.plans.free")}
          </h1>
        )}
      </div>
      <div className="flex flex-col justify-between items-start gap-6">
        <h3 className="text-sm font-medium text-start">{description}</h3>
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li
              key={index}
              className={cn("flex items-center gap-2 text-sm font-medium text-foreground ")}
            >
              <div className="w-5 h-5 rounded-full bg-success/15 flex items-center justify-center flex-shrink-0">
                <Check className="w-3 h-3 text-success" />
              </div>
              {feature}
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-auto">
        <PricingCardButton
          isActive={isActive}
          canUpgrade={canUpgrade}
          isContactUs={isContactUs}
          name={name}
          priceId={price?.id}
        />
      </div>
    </div>
  )
}

type PricingCardButtonProps = Pick<Props, "isActive" | "canUpgrade" | "isContactUs" | "name"> & {
  priceId?: string
}

const PricingCardButton = ({
  isActive,
  canUpgrade,
  isContactUs,
  name,
  priceId,
}: PricingCardButtonProps) => {
  const { t } = useTranslation()
  const { mutateAsync: contact } = useContactMutation()
  const { mutateAsync: createCheckoutSession } = useCreateCheckoutSessionMutation()

  if (isActive) {
    return (
      <Button variant="default" className="w-full" disabled>
        {t("subscription.plans.cta.active")}
      </Button>
    )
  }

  if (canUpgrade && !isContactUs) {
    if (!priceId) {
      return null
    }

    async function onClick() {
      if (!canUpgrade) {
        throw new Error("Cannot upgrade")
      }

      if (!priceId) {
        throw new Error(`Price ID not found for upgrade, plan name: ${name}`)
      }

      const url = await createCheckoutSession({
        mode: CheckoutSessionModeEnum.subscription,
        priceId,
      })

      window.location.href = url
    }

    return (
      <Button variant="default" className="w-full" disabled={!canUpgrade} onClick={onClick}>
        {t("subscription.plans.cta.upgrade", { name })}
      </Button>
    )
  }

  if (canUpgrade && isContactUs) {
    async function onClick() {
      if (!canUpgrade) {
        throw new Error("Cannot upgrade")
      }

      await contact({ reason: "GetInfo", plan: name })
      toast.success(t("subscription.plans.cta.contactSuccess"))
    }

    return (
      <Button variant="default" className="w-full" disabled={!canUpgrade} onClick={onClick}>
        {t("subscription.plans.cta.contactUs")}
      </Button>
    )
  }

  async function onClick() {
    await contact({ reason: "Downgrade", plan: name })
    toast.success(t("subscription.plans.cta.contactSuccess"))
  }

  return (
    <Button variant="outline" className="w-full" onClick={onClick}>
      {t("subscription.plans.cta.downgrade")}
    </Button>
  )
}
